import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { CopyableResourceBadge } from '@/components/ui/copyable-resource-badge';
import { JsonViewer } from '@/components/ui/json-viewer';
import { formatMoney } from "@/lib/money";
import { DateDisplay } from '@/components/date-display';
import { TransactionItemData } from './transaction-item';
import { TransactionStatusBadge, TransactionResultBadge } from './transaction-status-badge';
import { useOrgNavigate } from '@/hooks/use-app-navigate';
import {
  CheckCircle2,
  XCircle,
  Shield,
  AlertTriangle,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  CircleDashed,
} from 'lucide-react';

interface TransactionDetailsProps {
  transaction: TransactionItemData & {
    rawResponse?: any;
    rawRequest?: any;
    networkTransactionId?: string;
    paymentId?: string;
    storeId?: string;
    accountId?: string;
    storefrontTransactionId?: string;
    initiatedBy?: string;
    reasonType?: string;
    draft?: boolean;
    updatedAt?: string;
    paymentInstrumentId?: string;
    usedProcessorTokenId?: string;
    metadata?: any;
    requireAction?: any;
    cvvResponse?: {
      code?: string;
      message?: string;
      cvv_check_pass?: boolean;
    };
    avsResponse?: {
      code?: string;
      message?: string;
      address_check_pass?: boolean;
      postal_code_check_pass?: boolean;
    };
    threedsSession?: {
      status: string;
      messageVersion?: string;
      acsChallengeMandated?: boolean;
      authenticationType?: string;
      threeDSServerTransID?: string;
      dsTransID?: string;
      acsTransID?: string;
      authenticationValue?: string;
      eci?: string;
      liabilityShifted?: boolean;
      completedAt?: string;
      errorCode?: string;
      errorMessage?: string;
      transStatus?: string;
      transStatusLabel?: string;
      transStatusReason?: string;
    };
    usedProcessorToken?: {
      token: string;
      processor?: {
        name: string;
        id: string;
      };
    };
    paymentInstrument?: {
      type: string;
      token?: string;
    };
    isTest?: boolean;
  };
  currency: string;
}

// Capitalize first letter of a string
function capitalizeFirst(str: string): string {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
}

// Format transaction type with proper capitalization
function formatTransactionType(type: string): string {
  const typeMap: Record<string, string> = {
    purchase: 'Purchase',
    crypto_sale: 'Crypto Sale',
    refund: 'Refund',
    authorization: 'Authorization',
    authorize: 'Authorize',
    capture: 'Capture',
    void: 'Void',
    sale: 'Sale',
    auth: 'Auth',
    redirect_to_payment: 'Redirect To Payment',
    threeds: '3D Secure',
  };
  return typeMap[type] || capitalizeFirst(type);
}

// Format 3DS error messages
function format3DSErrorMessage(errorMessage: string) {
  try {
    const parsed = JSON.parse(errorMessage);
    if (parsed.errors) {
      return Object.entries(parsed.errors)
        .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
        .join('\n');
    }
    if (parsed.detail) {
      return `${parsed.detail}${parsed.title ? `\nReason: ${parsed.title}` : ''}`;
    }
    return JSON.stringify(parsed, null, 2);
  } catch {
    return errorMessage;
  }
}

// Get transaction status label
function getTransStatusLabel(status?: string): string {
  if (!status) return 'Unknown';
  const statusMap: Record<string, string> = {
    Y: 'Authentication Successful',
    N: 'Authentication Failed',
    U: 'Authentication Could Not Be Performed',
    A: 'Attempts Processing Performed',
    C: 'Challenge Required',
    D: 'Decoupled Authentication',
    R: 'Authentication Rejected',
    I: 'Information Only',
  };
  return statusMap[status] || status;
}

// Detail row component
function DetailRow({
  label,
  children,
  fullWidth = false,
}: {
  label: string;
  children: React.ReactNode;
  fullWidth?: boolean;
}) {
  return (
    <div className={`flex ${fullWidth ? 'flex-col gap-2' : 'items-start justify-between gap-4'}`}>
      <span className="text-muted-foreground min-w-[140px] text-sm font-medium">{label}</span>
      <div className={`${fullWidth ? '' : 'flex-1 text-right'} text-sm`}>{children}</div>
    </div>
  );
}

function CheckBadge({ passed }: { passed?: boolean }) {
  if (passed === true) {
    return (
      <div className="flex items-center gap-2">
        <CheckCircle2 className="h-4 w-4 text-green-600" />
        <Badge variant="green">Pass</Badge>
      </div>
    );
  }
  if (passed === false) {
    return (
      <div className="flex items-center gap-2">
        <XCircle className="h-4 w-4 text-red-600" />
        <Badge variant="destructive">Fail</Badge>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2">
      <CircleDashed className="h-4 w-4 text-muted-foreground" />
      <Badge variant="gray">Not checked</Badge>
    </div>
  );
}

const THREEDS_KEYS = new Set([
  'cavv', 'eci', 'three_ds_version', 'directory_server_id', 'cardholder_auth', 'xid',
]);
const SENSITIVE_KEYS = new Set(['security_key', 'api_key', 'password', 'secret']);

function ParsedRawRequest({ rawRequest }: { rawRequest: { method?: string; url?: string; data?: string } }) {
  if (!rawRequest?.data || typeof rawRequest.data !== 'string' || !rawRequest.data.includes('=')) return null;

  let params: [string, string][];
  try {
    params = Array.from(new URLSearchParams(rawRequest.data).entries());
  } catch {
    return null;
  }
  if (params.length === 0) return null;

  const threeDsParams = params.filter(([k]) => THREEDS_KEYS.has(k));
  const otherParams = params.filter(([k]) => !THREEDS_KEYS.has(k));

  return (
    <div className="space-y-3">
      {rawRequest.url && (
        <div className="flex items-center gap-2 text-xs">
          <Badge variant="outline" className="font-mono text-[10px] uppercase">{rawRequest.method || 'POST'}</Badge>
          <span className="text-muted-foreground truncate font-mono">{rawRequest.url}</span>
        </div>
      )}
      {threeDsParams.length > 0 && (
        <div className="rounded-lg border border-green-200 bg-green-50 p-3 dark:border-green-900 dark:bg-green-900/10">
          <div className="mb-2 flex items-center gap-1.5">
            <Shield className="h-3.5 w-3.5 text-green-600 dark:text-green-400" />
            <span className="text-xs font-semibold text-green-700 dark:text-green-300">3DS Values Sent</span>
          </div>
          <div className="space-y-1">
            {threeDsParams.map(([k, v]) => (
              <div key={k} className="flex items-center justify-between gap-3 text-xs">
                <span className="font-medium text-green-700 dark:text-green-300">{k}</span>
                <span className="truncate font-mono text-green-800 dark:text-green-200">{v}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="max-h-48 space-y-0.5 overflow-y-auto rounded-lg border p-2">
        {otherParams.map(([k, v]) => (
          <div key={k} className="flex items-start justify-between gap-3 py-0.5 text-xs">
            <span className="text-muted-foreground shrink-0">{k}</span>
            <span className={`truncate text-right font-mono ${SENSITIVE_KEYS.has(k) ? 'text-muted-foreground/40' : ''}`}>
              {SENSITIVE_KEYS.has(k) ? '[REDACTED]' : v}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function TransactionDetails({ transaction, currency }: TransactionDetailsProps) {
  const isRefund = transaction.type === 'refund';
  const [showRawResponse, setShowRawResponse] = useState(false);
  const [showRawRequest, setShowRawRequest] = useState(false);
  const has3DS = transaction.threedsSession;
  const { orgRoutes } = useOrgNavigate();

  return (
    <div className="bg-muted/30 space-y-6 rounded-lg border p-4">
      {/* Test Mode Warning */}
      {transaction.isTest && (
        <div className="flex items-start gap-3 rounded-lg border border-yellow-200 bg-yellow-50 p-3 dark:border-yellow-900 dark:bg-yellow-900/10">
          <AlertTriangle className="mt-0.5 h-4 w-4 text-yellow-600 dark:text-yellow-400" />
          <div className="flex-1">
            <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">Test Transaction</p>
            <p className="text-muted-foreground mt-1 text-xs">
              This transaction was processed in test mode. No real charges were made.
            </p>
          </div>
        </div>
      )}

      {/* Basic Information */}
      <div className="space-y-3">
        <h4 className="text-sm font-semibold">Transaction Information</h4>
        <div className="space-y-2">
          <DetailRow label="Transaction ID" fullWidth>
            <CopyableResourceBadge name="Transaction" value={transaction.id} variant="outline" />
          </DetailRow>

          {transaction.externalTransactionId && (
            <DetailRow label="External ID" fullWidth>
              <CopyableResourceBadge
                name="External ID"
                value={transaction.externalTransactionId}
                variant="outline"
              />
            </DetailRow>
          )}

          <DetailRow label="Type">
            <span>{formatTransactionType(transaction.type)}</span>
          </DetailRow>

          <DetailRow label="Amount">
            <span className="font-semibold">{formatMoney(transaction.amount, currency)}</span>
          </DetailRow>

          <DetailRow label="Status">
            <TransactionStatusBadge status={transaction.status} result={transaction.result} />
          </DetailRow>

          {transaction.result && (
            <DetailRow label="Result">
              <TransactionResultBadge result={transaction.result} />
            </DetailRow>
          )}

          <DetailRow label="Created">
            <span><DateDisplay value={transaction.createdAt} /></span>
          </DetailRow>

          {transaction.authorizationCode && (
            <DetailRow label="Authorization Code">
              <span className="font-mono text-xs">{transaction.authorizationCode}</span>
            </DetailRow>
          )}

          {transaction.updatedAt && (
            <DetailRow label="Updated">
              <span><DateDisplay value={transaction.updatedAt} /></span>
            </DetailRow>
          )}

          {transaction.networkTransactionId && (
            <DetailRow label="Network Transaction ID" fullWidth>
              <CopyableResourceBadge
                name="Network Txn"
                value={transaction.networkTransactionId}
                variant="outline"
              />
            </DetailRow>
          )}

          {transaction.paymentId && (
            <DetailRow label="Payment ID" fullWidth>
              <CopyableResourceBadge name="Payment" value={transaction.paymentId} variant="outline" />
            </DetailRow>
          )}

          {transaction.paymentInstrumentId && (
            <DetailRow label="Payment Instrument ID" fullWidth>
              <CopyableResourceBadge
                name="Payment Instrument"
                value={transaction.paymentInstrumentId}
                variant="outline"
              />
            </DetailRow>
          )}

          {transaction.storeId && (
            <DetailRow label="Store ID" fullWidth>
              <CopyableResourceBadge name="Store" value={transaction.storeId} variant="outline" />
            </DetailRow>
          )}

          {transaction.accountId && (
            <DetailRow label="Account ID" fullWidth>
              <CopyableResourceBadge name="Account" value={transaction.accountId} variant="outline" />
            </DetailRow>
          )}

          {transaction.storefrontTransactionId && (
            <DetailRow label="Storefront Transaction ID" fullWidth>
              <CopyableResourceBadge
                name="Storefront Txn"
                value={transaction.storefrontTransactionId}
                variant="outline"
              />
            </DetailRow>
          )}

          {transaction.initiatedBy && (
            <DetailRow label="Initiated By">
              <Badge variant="outline">{capitalizeFirst(transaction.initiatedBy)}</Badge>
            </DetailRow>
          )}

          {transaction.reasonType && (
            <DetailRow label="Reason Type">
              <span>{capitalizeFirst(transaction.reasonType)}</span>
            </DetailRow>
          )}

          {transaction.draft !== undefined && (
            <DetailRow label="Test Mode">
              <Badge variant={transaction.draft ? 'yellow' : 'outline'}>
                {transaction.draft ? 'Test' : 'Live'}
              </Badge>
            </DetailRow>
          )}

          {transaction.requireAction && (
            <DetailRow label="Require Action" fullWidth>
              <JsonViewer data={transaction.requireAction} variant="warning" />
            </DetailRow>
          )}

          {transaction.metadata && (
            <DetailRow label="Metadata" fullWidth>
              <JsonViewer data={transaction.metadata} />
            </DetailRow>
          )}
        </div>
      </div>

      {/* Processor Information */}
      {transaction.processor && (
        <>
          <Separator />
          <div className="space-y-3">
            <h4 className="text-sm font-semibold">Processor Information</h4>
            <div className="space-y-2">
              <DetailRow label="Processor">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{transaction.processor.name}</span>
                  {(transaction.processor as any).type && (
                    <Badge variant="outline" className="text-[10px]">{(transaction.processor as any).type}</Badge>
                  )}
                  <a
                    href={orgRoutes.PAYMENT_FLOWS() + '?tab=processors'}
                    className="text-blue-600 hover:underline"
                  >
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              </DetailRow>
              <DetailRow label="Processor ID" fullWidth>
                <CopyableResourceBadge name="Processor" value={transaction.processor.id} variant="outline" />
              </DetailRow>
            </div>
          </div>
        </>
      )}

      {/* Payment Data Source */}
      {!isRefund && (transaction.usedProcessorToken || transaction.paymentInstrument) && (
        <>
          <Separator />
          <div className="space-y-3">
            <h4 className="text-sm font-semibold">Payment Data Source</h4>
            <div className="space-y-2">
              {transaction.usedProcessorToken ? (
                <>
                  <DetailRow label="Source Type">
                    <Badge variant="outline">Processor Token</Badge>
                  </DetailRow>
                  <DetailRow label="Processor">
                    <span>{transaction.usedProcessorToken.processor?.name || 'Unknown'}</span>
                  </DetailRow>
                  <DetailRow label="Token" fullWidth>
                    <CopyableResourceBadge
                      name="Token"
                      value={transaction.usedProcessorToken.token}
                      variant="outline"
                    />
                  </DetailRow>
                  {transaction.usedProcessorTokenId && (
                    <DetailRow label="Token ID" fullWidth>
                      <CopyableResourceBadge
                        name="Processor Token"
                        value={transaction.usedProcessorTokenId}
                        variant="outline"
                      />
                    </DetailRow>
                  )}
                </>
              ) : (
                <DetailRow label="Source Type">
                  <div className="flex items-center gap-2">
                    <Shield className="h-3 w-3 text-green-600" />
                    <Badge variant="green">Secure Vault</Badge>
                    <span className="text-muted-foreground text-xs">Encrypted payment data</span>
                  </div>
                </DetailRow>
              )}
            </div>
          </div>
        </>
      )}

      {/* Error Information */}
      {transaction.error && (
        <>
          <Separator />
          <div className="space-y-3">
            <h4 className="flex items-center gap-2 text-sm font-semibold text-red-600 dark:text-red-400">
              <XCircle className="h-4 w-4" />
              Error Details
            </h4>
            <div className="space-y-2">
              {transaction.error.processorCode && (
                <DetailRow label="Processor Error Code">
                  <Badge variant="destructive">{transaction.error.processorCode}</Badge>
                </DetailRow>
              )}
              {transaction.error.processorMessage && (
                <DetailRow label="Processor Error Message" fullWidth>
                  <div className="text-destructive rounded-md bg-red-50 p-2 text-left text-xs dark:bg-red-900/10">
                    {transaction.error.processorMessage}
                  </div>
                </DetailRow>
              )}
              <DetailRow label="Error Code">
                <Badge variant="destructive">{transaction.error.code}</Badge>
              </DetailRow>
              <DetailRow label="Error Message" fullWidth>
                <div className="text-destructive rounded-md bg-red-50 p-2 text-left text-xs dark:bg-red-900/10">
                  {transaction.error.message}
                </div>
              </DetailRow>
            </div>
          </div>
        </>
      )}

      {/* CVV Response */}
      {transaction.cvvResponse && !isRefund && (
        <>
          <Separator />
          <div className="space-y-3">
            <h4 className="text-sm font-semibold">CVV Verification</h4>
            <div className="space-y-2">
              {transaction.cvvResponse.code && (
                <DetailRow label="Response Code">
                  <span className="font-mono text-xs">{transaction.cvvResponse.code}</span>
                </DetailRow>
              )}
              {transaction.cvvResponse.message && (
                <DetailRow label="Message">
                  <span>{transaction.cvvResponse.message}</span>
                </DetailRow>
              )}
              <DetailRow label="CVV Check">
                <CheckBadge passed={transaction.cvvResponse.cvv_check_pass} />
              </DetailRow>
            </div>
          </div>
        </>
      )}

      {/* AVS Response */}
      {transaction.avsResponse && !isRefund && (
        <>
          <Separator />
          <div className="space-y-3">
            <h4 className="text-sm font-semibold">Address Verification (AVS)</h4>
            <div className="space-y-2">
              {transaction.avsResponse.code && (
                <DetailRow label="Response Code">
                  <span className="font-mono text-xs">{transaction.avsResponse.code}</span>
                </DetailRow>
              )}
              {transaction.avsResponse.message && (
                <DetailRow label="Message">
                  <span>{transaction.avsResponse.message}</span>
                </DetailRow>
              )}
              <DetailRow label="Address Check">
                <CheckBadge passed={transaction.avsResponse.address_check_pass} />
              </DetailRow>
              <DetailRow label="Postal Code Check">
                <CheckBadge passed={transaction.avsResponse.postal_code_check_pass} />
              </DetailRow>
            </div>
          </div>
        </>
      )}

      {/* 3D Secure Information */}
      {has3DS && transaction.threedsSession && (
        <>
          <Separator />
          <div className="space-y-3">
            <h4 className="flex items-center gap-2 text-sm font-semibold">
              <Shield className="h-4 w-4 text-blue-600" />
              3D Secure Authentication
              {transaction.threedsSession.status === 'failed' && (
                <Badge variant="destructive" className="ml-2">
                  Failed
                </Badge>
              )}
            </h4>
            <div className="space-y-2">
              <DetailRow label="Status">
                <TransactionStatusBadge status={transaction.threedsSession.status} />
              </DetailRow>

              <DetailRow label="Protocol Version">
                <span>{transaction.threedsSession.messageVersion || 'N/A'}</span>
              </DetailRow>

              <DetailRow label="Challenge Required">
                <Badge variant={transaction.threedsSession.acsChallengeMandated ? 'yellow' : 'green'}>
                  {transaction.threedsSession.acsChallengeMandated ? 'Yes' : 'No'}
                </Badge>
              </DetailRow>

              {transaction.threedsSession.authenticationType && (
                <DetailRow label="Authentication Type">
                  <span>{transaction.threedsSession.authenticationType}</span>
                </DetailRow>
              )}

              <DetailRow label="Liability Shifted">
                <Badge variant={transaction.threedsSession.liabilityShifted ? 'green' : 'yellow'}>
                  {transaction.threedsSession.liabilityShifted ? 'Yes' : 'No'}
                </Badge>
              </DetailRow>

              {transaction.threedsSession.transStatus && (
                <DetailRow label="Transaction Status">
                  <div className="flex flex-col items-end gap-1">
                    <Badge
                      variant={
                        transaction.threedsSession.transStatus === 'Y'
                          ? 'green'
                          : transaction.threedsSession.transStatus === 'N'
                            ? 'destructive'
                            : transaction.threedsSession.transStatus === 'C'
                              ? 'yellow'
                              : 'outline'
                      }
                    >
                      {transaction.threedsSession.transStatusLabel ||
                        getTransStatusLabel(transaction.threedsSession.transStatus)}
                    </Badge>
                    {transaction.threedsSession.transStatusReason && (
                      <span className="text-muted-foreground text-xs">
                        Reason: {transaction.threedsSession.transStatusReason}
                      </span>
                    )}
                  </div>
                </DetailRow>
              )}

              {transaction.threedsSession.eci && (
                <DetailRow label="ECI">
                  <span className="font-mono text-xs">{transaction.threedsSession.eci}</span>
                </DetailRow>
              )}

              {transaction.threedsSession.completedAt && (
                <DetailRow label="Completed At">
                  <span><DateDisplay value={transaction.threedsSession.completedAt} /></span>
                </DetailRow>
              )}

              {/* 3DS Error Information */}
              {(transaction.threedsSession.errorCode || transaction.threedsSession.errorMessage) && (
                <>
                  <Separator className="my-2" />
                  <DetailRow label="3DS Error Code">
                    <Badge variant="destructive">{transaction.threedsSession.errorCode}</Badge>
                  </DetailRow>
                  {transaction.threedsSession.errorMessage && (
                    <DetailRow label="Error Details" fullWidth>
                      <pre className="text-destructive overflow-x-auto rounded-md bg-red-50 p-2 text-left text-xs dark:bg-red-900/10">
                        {format3DSErrorMessage(transaction.threedsSession.errorMessage)}
                      </pre>
                    </DetailRow>
                  )}
                </>
              )}

              {/* 3DS Transaction IDs */}
              {transaction.threedsSession.threeDSServerTransID && (
                <>
                  <Separator className="my-2" />
                  <DetailRow label="3DS Server Trans ID" fullWidth>
                    <CopyableResourceBadge
                      name="3DS Server Trans"
                      value={transaction.threedsSession.threeDSServerTransID}
                      variant="outline"
                    />
                  </DetailRow>
                </>
              )}

              {transaction.threedsSession.dsTransID && (
                <DetailRow label="DS Trans ID" fullWidth>
                  <CopyableResourceBadge
                    name="DS Trans"
                    value={transaction.threedsSession.dsTransID}
                    variant="outline"
                  />
                </DetailRow>
              )}

              {transaction.threedsSession.acsTransID && (
                <DetailRow label="ACS Trans ID" fullWidth>
                  <CopyableResourceBadge
                    name="ACS Trans"
                    value={transaction.threedsSession.acsTransID}
                    variant="outline"
                  />
                </DetailRow>
              )}

              {transaction.threedsSession.authenticationValue && (
                <DetailRow label="Auth Value" fullWidth>
                  <CopyableResourceBadge
                    name="Auth Value"
                    value={transaction.threedsSession.authenticationValue}
                    variant="outline"
                  />
                </DetailRow>
              )}
            </div>
          </div>
        </>
      )}

      {/* Raw Response */}
      {transaction.rawResponse && (
        <>
          <Separator />
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold">Processor Raw Response</h4>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowRawResponse(!showRawResponse)}
                className="h-7 gap-1 text-xs"
              >
                {showRawResponse ? (
                  <>
                    <ChevronUp className="h-3 w-3" />
                    Hide
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-3 w-3" />
                    Show
                  </>
                )}
              </Button>
            </div>
            {showRawResponse && <JsonViewer data={transaction.rawResponse} title="Copy Response" />}
          </div>
        </>
      )}

      {/* Raw Request */}
      {transaction.rawRequest && (
        <>
          <Separator />
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold">Processor Raw Request</h4>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowRawRequest(!showRawRequest)}
                className="h-7 gap-1 text-xs"
              >
                {showRawRequest ? (
                  <>
                    <ChevronUp className="h-3 w-3" />
                    Hide
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-3 w-3" />
                    Show
                  </>
                )}
              </Button>
            </div>
            {showRawRequest && (
              <>
                <ParsedRawRequest rawRequest={transaction.rawRequest} />
                <JsonViewer data={transaction.rawRequest} title="Copy Request" />
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}
